import { useState, useEffect } from "react";
import { statusLabel, statusColor, BADGE_STATUSES, PRE_TORK_STATUSES, PLANK_TYPES } from "./constants";
import TorkPicker from "./TorkPicker";
import OrderStatusPicker from "./OrderStatusPicker";
import ApproveDenyModal from "../../components/RootElements/ApproveDenyModal";

const torkById = (torks, torkId) => torks.find(t => t.id === torkId);
const torkName = (torks, torkId) => torkById(torks, torkId)?.name || "–";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("sv-SE") : "–";
const toDateInput = (d) => d ? new Date(d).toISOString().slice(0, 10) : "";
const isInTork = (o) => BADGE_STATUSES.includes(o.status);

const patchOrder = (order, field, value) => {
    if (field.startsWith("package.")) {
        const key = field.slice("package.".length);
        return { ...order, package: { ...order.package, [key]: value || null } };
    }
    return { ...order, [field]: value };
};

const BORDER_COLORS = {
    planned: "border-l-gray-300",
    unplanned: "border-l-purple-400",
    closednotstarted: "border-l-amber-400",
    ongoing: "border-l-blue-500",
    open: "border-l-indigo-500",
    sample: "border-l-yellow-400",
    ready: "border-l-green-500",
    picking: "border-l-orange-500",
    picked: "border-l-teal-500",
    closed: "border-l-gray-400",
};

const PRIO_COLORS = {
    1: "bg-red-500 text-white",
    2: "bg-orange-400 text-white",
    3: "bg-yellow-300 text-gray-800",
};

const borderColor = (status) => BORDER_COLORS[status] || "border-l-gray-200";
const prioColor = (prio) => PRIO_COLORS[prio] || "bg-gray-200 text-gray-700";

/* Shows a progress bar and "Klar om / Försenad" for orders actively drying. */
const DryingProgress = ({ dryStarted, dryFinished }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);

    const start = new Date(dryStarted).getTime();
    const end = new Date(dryFinished).getTime();
    const total = end - start;
    if (total <= 0) return null;

    const progress = Math.min(Math.max((now - start) / total, 0), 1);
    const remainingMs = end - now;
    const isLate = remainingMs < 0;
    const absMs = Math.abs(remainingMs);
    const h = Math.floor(absMs / 3_600_000);
    const min = Math.floor((absMs % 3_600_000) / 60_000);
    const timeStr = h > 0 ? `${h}h ${min}min` : `${min}min`;
    const label = isLate ? `Försenad ${timeStr}` : `Klar om ${timeStr}`;

    return (
        <span className="flex items-center gap-2 ml-auto">
            <div className="relative w-20 h-2 bg-gray-200 rounded-full overflow-hidden shrink-0">
                <div
                    className={`absolute left-0 top-0 h-2 rounded-full transition-all ${isLate ? "bg-red-500" : "bg-blue-500"}`}
                    style={{ width: `${progress * 100}%` }}
                />
            </div>
            <span className={`text-xs font-medium whitespace-nowrap ${isLate ? "text-red-600" : "text-blue-700"}`}>
                {label}
            </span>
        </span>
    );
};

const noteStatusColor = (status) =>
    ({ planned: "bg-blue-100 text-blue-700", ongoing: "bg-amber-100 text-amber-700" }[status] || "");

const noteStatusLabel = (status) =>
    ({ planned: "Planerad", ongoing: "Pågående" }[status] || status);

const TypeDot = ({ noteTypes, type }) => {
    const color = (noteTypes || []).find(nt => nt.name === type)?.color || "#9CA3AF";
    return <span className="inline-block w-2 h-2 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: color }} />;
};

/* Triangle badge with hover tooltip showing active notes for a tork. */
const TorkNotesBadge = ({ tork, noteTypes }) => {
    const [hovered, setHovered] = useState(false);
    const activeNotes = (tork?.notes || []).filter(n => n.status === "planned" || n.status === "ongoing");
    if (!activeNotes.length) return null;

    return (
        <span
            className="relative inline-flex items-center"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <span className="text-amber-500 text-xs leading-none cursor-default">⚠</span>
            {hovered && (
                <div className="absolute left-0 top-full mt-1 z-30 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs text-start">
                    <p className="font-semibold text-gray-700 mb-1.5">Noteringar</p>
                    <ul className="space-y-1.5">
                        {activeNotes.map(n => (
                            <li key={n.id} className="flex items-start gap-1.5 text-gray-600">
                                <TypeDot noteTypes={noteTypes} type={n.type} />
                                <span className="flex-1">{n.type} – {n.note}</span>
                                <span className={"flex-shrink-0 px-1.5 py-0.5 rounded " + noteStatusColor(n.status)}>
                                    {noteStatusLabel(n.status)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </span>
    );
};

/* Inline-editable text field used in the secondary row. */
const InlineField = ({ label, displayValue, placeholder, editing, onStartEdit, children }) => (
    <span className="flex items-center gap-1">
        <span className="text-gray-400">{label}:</span>
        {editing ? children : (
            <span
                className={onStartEdit ? "cursor-pointer text-gray-700 hover:underline hover:decoration-dotted hover:text-blue-700" : "text-gray-700"}
                onClick={onStartEdit}
            >
                {displayValue || <span className="text-gray-400">{placeholder || "–"}</span>}
            </span>
        )}
    </span>
);

/* All non-closed orders: planned (actionable) + in-tork (status indicator only). */
const OrdersTable = ({ orders, torks, noteTypes, onUpdate, onStart, onStatusChange, onDelete, onShowHistory, onBulkUpdatePrios, activeNoteTorkIds, search = "", readOnly = false }) => {
    const [editingCell, setEditingCell] = useState(null); // { id, field }
    const [editValue, setEditValue] = useState("");
    const [editOriginal, setEditOriginal] = useState("");
    const [torkPickerOrderId, setTorkPickerOrderId] = useState(null);
    const [statusPickerOrderId, setStatusPickerOrderId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);

    const visible = orders
        .filter(o => o.status === "planned" || o.status === "unplanned" || o.status === "closednotstarted")
        .sort((a, b) => (a.prio || 0) - (b.prio || 0));

    // Filtered view only — `visible` stays the full list so prio renumbering stays correct.
    const query = (search || "").trim().toLowerCase();
    const filtered = query
        ? visible.filter(o => (o.orderId || "").toLowerCase().includes(query))
        : visible;

    if (visible.length === 0) {
        return <p className="text-gray-500 p-4">Inga aktiva ordrar.</p>;
    }

    if (filtered.length === 0) {
        return <p className="text-gray-500 p-4">Inga ordrar matchar sökningen.</p>;
    }

    const startEdit = (order, field, editInitialValue) => {
        const v = editInitialValue ?? "";
        setEditingCell({ id: order.id, field });
        setEditValue(v);
        setEditOriginal(v);
    };

    /* Rerank all pre-tork orders when a prio is changed. */
    const commitPrioEdit = (order) => {
        if (!editingCell || editingCell.id !== order.id || editingCell.field !== "prio") return;
        setEditingCell(null);
        if (editValue === editOriginal) return;

        const rawPrio = Math.max(1, Number(editValue) || 1);
        const newPrio = Math.min(rawPrio, visible.length);

        // Reinsert the changed order at the new position
        const rest = visible.filter(o => o.id !== order.id);
        const reordered = [
            ...rest.slice(0, newPrio - 1),
            order,
            ...rest.slice(newPrio - 1),
        ];

        // Build updated prio list (1-based sequential)
        const newPrioList = reordered.map((o, i) => ({ id: o.id, prio: i + 1 }));

        onBulkUpdatePrios(newPrioList);
    };

    const commitEdit = (order, field, transform) => {
        if (!editingCell || editingCell.id !== order.id || editingCell.field !== field) return;
        setEditingCell(null);
        if (editValue === editOriginal) return;
        const value = transform ? transform(editValue) : editValue;
        onUpdate(patchOrder(order, field, value));
    };

    const cancelEdit = () => setEditingCell(null);
    const isEditing = (order, field) => editingCell?.id === order.id && editingCell?.field === field;

    const handleStatusChange = (order, status) => {
        if (status === "closednotstarted" && !order.torkId) {
            setPendingAction({ order, action: "status", status });
            return;
        }
        onStatusChange(order.id, status);
    };

    const handleStart = (order) => {
        if (!order.torkId) {
            setPendingAction({ order, action: "start" });
            return;
        }
        onStart(order);
    };

    const commitPendingAction = (torkId) => {
        if (!pendingAction) return;
        const { order, action, status } = pendingAction;
        onUpdate(patchOrder(order, "torkId", torkId));
        if (action === "status") onStatusChange(order.id, status);
        else onStart({ ...order, torkId });
        setPendingAction(null);
    };

    return (
        <>
        <div className="flex flex-col gap-2">
            {filtered.map(o => {
                const inTork = isInTork(o);
                const bgClass = inTork
                    ? "bg-blue-50"
                    : o.status === "closednotstarted"
                        ? "bg-amber-50"
                        : o.status === "unplanned"
                            ? "bg-purple-50"
                            : "bg-white";

                return (
                    <div
                        key={o.id}
                        className={`rounded-lg border border-gray-200 border-l-4 ${borderColor(o.status)} ${bgClass} shadow-sm`}
                    >
                        {/* ── Primary row ────────────────────────────────── */}
                        <div className="flex items-center gap-3 px-4 py-3 flex-wrap">

                            {/* Priority badge */}
                            {isEditing(o, "prio") ? (
                                <input
                                    autoFocus
                                    type="number" min="1" max={visible.length}
                                    className="border rounded px-1 py-0.5 w-14 text-sm"
                                    value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onBlur={() => commitPrioEdit(o)}
                                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") cancelEdit(); }}
                                />
                            ) : (
                                <span
                                    onClick={readOnly ? undefined : () => startEdit(o, "prio", String(o.prio))}
                                    title={readOnly ? `Prio ${o.prio}` : `Prio ${o.prio} — klicka för att ändra`}
                                    className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${prioColor(o.prio)} ${!readOnly ? "cursor-pointer" : ""}`}
                                >
                                    {o.prio}
                                </span>
                            )}

                            {/* Order-ID */}
                            <div className="font-semibold text-gray-900 text-sm min-w-0">
                                {isEditing(o, "orderId") ? (
                                    <input
                                        autoFocus
                                        className="border rounded px-1 py-0.5 w-32 text-sm font-semibold"
                                        value={editValue}
                                        onChange={e => setEditValue(e.target.value)}
                                        onBlur={() => commitEdit(o, "orderId")}
                                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") cancelEdit(); }}
                                    />
                                ) : (
                                    <span
                                        className={!readOnly ? "cursor-pointer hover:underline hover:decoration-dotted hover:text-blue-700" : ""}
                                        onClick={readOnly ? undefined : () => startEdit(o, "orderId", o.orderId || "")}
                                    >
                                        {o.orderId || <span className="text-gray-400 font-normal italic">Order-ID</span>}
                                    </span>
                                )}
                            </div>

                            {/* Type badge */}
                            {isEditing(o, "type") ? (
                                <select
                                    autoFocus
                                    className="border rounded px-1 py-0.5 text-sm"
                                    value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onBlur={() => commitEdit(o, "type")}
                                    onKeyDown={e => { if (e.key === "Escape") cancelEdit(); }}
                                >
                                    {PLANK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            ) : (
                                <span
                                    onClick={readOnly ? undefined : () => startEdit(o, "type", o.type || PLANK_TYPES[0])}
                                    className={`px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium shrink-0 ${!readOnly ? "cursor-pointer hover:bg-gray-200" : ""}`}
                                >
                                    {o.type || "–"}
                                </span>
                            )}

                            {/* Tork */}
                            <div className="relative">
                                {readOnly ? (
                                    <span className="flex items-center gap-1 text-sm text-gray-600">
                                        <span className="text-gray-400 text-xs shrink-0">Tork:</span>
                                        <span>{torkName(torks, o.torkId)}</span>
                                        {o.torkId && activeNoteTorkIds.includes(o.torkId) && (
                                            <TorkNotesBadge tork={torkById(torks, o.torkId)} noteTypes={noteTypes} />
                                        )}
                                    </span>
                                ) : (
                                <button
                                    onClick={() => setTorkPickerOrderId(o.id)}
                                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-blue-700 hover:underline hover:decoration-dotted"
                                >
                                    <span className="text-gray-400 text-xs shrink-0">Tork:</span>
                                    <span>{torkName(torks, o.torkId)}</span>
                                    {o.torkId && activeNoteTorkIds.includes(o.torkId) && (
                                        <TorkNotesBadge tork={torkById(torks, o.torkId)} noteTypes={noteTypes} />
                                    )}
                                </button>
                                )}
                                {torkPickerOrderId === o.id && (
                                    <TorkPicker
                                        torks={torks}
                                        noteTypes={noteTypes || []}
                                        value={o.torkId || null}
                                        onChange={(torkId) => {
                                            onUpdate(patchOrder(o, "torkId", torkId));
                                        }}
                                        onClose={() => setTorkPickerOrderId(null)}
                                    />
                                )}
                            </div>

                            <div className="flex-1" />

                            {/* Status badge */}
                            <div className="relative shrink-0">
                                {PRE_TORK_STATUSES.includes(o.status) && !readOnly ? (
                                    <>
                                        <button
                                            onClick={() => setStatusPickerOrderId(o.id)}
                                            title="Ändra status"
                                            className={"px-2.5 py-1 rounded-full text-xs font-medium hover:ring-2 hover:ring-blue-300 transition " + statusColor(o.status)}
                                        >
                                            {statusLabel(o.status)}
                                        </button>
                                        {statusPickerOrderId === o.id && (
                                            <OrderStatusPicker
                                                value={o.status}
                                                onStatusChange={(status) => handleStatusChange(o, status)}
                                                onStart={() => handleStart(o)}
                                                onClose={() => setStatusPickerOrderId(null)}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <span className={"px-2.5 py-1 rounded-full text-xs font-medium " + statusColor(o.status)}>
                                        {statusLabel(o.status)}
                                    </span>
                                )}
                            </div>

                            {/* Action buttons */}
                            <div className="flex gap-2 shrink-0">
                                {!readOnly && !inTork && o.status !== "closednotstarted" && (
                                    <button
                                        onClick={() => setConfirmDeleteId(o.id)}
                                        className="px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 text-xs transition"
                                    >
                                        Ta bort
                                    </button>
                                )}
                                <button
                                    onClick={() => onShowHistory(o)}
                                    className="px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-100 text-xs transition"
                                >
                                    Historik
                                </button>
                            </div>
                        </div>

                        {/* ── Comment row ─────────────────────────────────── */}
                        {(o.comment || !readOnly) && (
                            <div className="px-4 py-2 border-t border-gray-100">
                                {isEditing(o, "comment") ? (
                                    <textarea
                                        autoFocus
                                        rows={2}
                                        className="border rounded px-2 py-1 w-full text-xs text-gray-700 resize-none"
                                        value={editValue}
                                        placeholder="Skriv en kommentar..."
                                        onChange={e => setEditValue(e.target.value)}
                                        onBlur={() => commitEdit(o, "comment", v => v.trim() || null)}
                                        onKeyDown={e => { if (e.key === "Escape") cancelEdit(); if (e.key === "Enter" && e.ctrlKey) e.target.blur(); }}
                                    />
                                ) : (
                                    <span
                                        className={`text-xs flex items-start gap-1.5 ${!readOnly ? "cursor-pointer group" : ""}`}
                                        onClick={readOnly ? undefined : () => startEdit(o, "comment", o.comment || "")}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h6m-6 4h8M5 4h14a2 2 0 012 2v10a2 2 0 01-2 2H7l-4 4V6a2 2 0 012-2z" />
                                        </svg>
                                        {o.comment
                                            ? <span className={`text-gray-700 ${!readOnly ? "group-hover:underline group-hover:decoration-dotted group-hover:text-blue-700" : ""}`}>{o.comment}</span>
                                            : <span className="text-gray-400 italic">Lägg till kommentar...</span>
                                        }
                                    </span>
                                )}
                            </div>
                        )}

                        {/* ── Secondary row ───────────────────────────────── */}
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2 text-xs text-gray-500 border-t border-gray-100">

                            <InlineField
                                label="Dim"
                                displayValue={o.package?.dimension}
                                editing={isEditing(o, "package.dimension")}
                                onStartEdit={readOnly ? undefined : () => startEdit(o, "package.dimension", o.package?.dimension || "")}
                            >
                                <input
                                    autoFocus
                                    className="border rounded px-1 py-0.5 w-24 text-xs"
                                    value={editValue}
                                    placeholder="62x100"
                                    onChange={e => setEditValue(e.target.value)}
                                    onBlur={() => commitEdit(o, "package.dimension")}
                                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") cancelEdit(); }}
                                />
                            </InlineField>

                            <InlineField
                                label="Fukt"
                                displayValue={o.package?.moisture}
                                editing={isEditing(o, "package.moisture")}
                                onStartEdit={readOnly ? undefined : () => startEdit(o, "package.moisture", o.package?.moisture ? String(parseFloat(o.package.moisture)) : "")}
                            >
                                <span className="flex items-center gap-0.5">
                                    <input
                                        autoFocus
                                        type="number" min="0" max="100" step="0.1"
                                        className="border rounded px-1 py-0.5 w-16 text-xs"
                                        value={editValue}
                                        onChange={e => setEditValue(e.target.value)}
                                        onBlur={() => commitEdit(o, "package.moisture", v => v !== "" ? v + "%" : null)}
                                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") cancelEdit(); }}
                                    />
                                    <span className="text-xs text-gray-600">%</span>
                                </span>
                            </InlineField>

                            {o.package?.materialId && (
                                <InlineField
                                    label="Material"
                                    displayValue={o.package.materialId}
                                    editing={isEditing(o, "package.materialId")}
                                    onStartEdit={readOnly ? undefined : () => startEdit(o, "package.materialId", o.package.materialId || "")}
                                >
                                    <input
                                        autoFocus
                                        className="border rounded px-1 py-0.5 w-28 text-xs"
                                        value={editValue}
                                        onChange={e => setEditValue(e.target.value)}
                                        onBlur={() => commitEdit(o, "package.materialId", v => v || null)}
                                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") cancelEdit(); }}
                                    />
                                </InlineField>
                            )}

                            <InlineField
                                label="Sågad"
                                displayValue={fmtDate(o.package?.sawedAt)}
                                editing={isEditing(o, "package.sawedAt")}
                                onStartEdit={readOnly ? undefined : () => startEdit(o, "package.sawedAt", toDateInput(o.package?.sawedAt))}
                            >
                                <input
                                    autoFocus
                                    type="date"
                                    className="border rounded px-1 py-0.5 text-xs"
                                    value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onBlur={() => commitEdit(o, "package.sawedAt", v => v || null)}
                                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") cancelEdit(); }}
                                />
                            </InlineField>

                            <InlineField
                                label="Fot"
                                displayValue={o.pickupLocation}
                                editing={isEditing(o, "pickupLocation")}
                                onStartEdit={readOnly ? undefined : () => startEdit(o, "pickupLocation", o.pickupLocation || "")}
                            >
                                <input
                                    autoFocus
                                    className="border rounded px-1 py-0.5 w-24 text-xs"
                                    value={editValue}
                                    placeholder="102-20"
                                    onChange={e => setEditValue(e.target.value)}
                                    onBlur={() => commitEdit(o, "pickupLocation", v => v || null)}
                                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") cancelEdit(); }}
                                />
                            </InlineField>

                            {/* Drying progress — only shown for in-tork orders with both timestamps */}
                            {inTork && o.dates?.dryStarted && o.dates?.dryFinished && (
                                <DryingProgress dryStarted={o.dates.dryStarted} dryFinished={o.dates.dryFinished} />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>

        {confirmDeleteId && (
            <ApproveDenyModal
                onAccept={() => { onDelete(confirmDeleteId); setConfirmDeleteId(null); }}
                onDeny={() => setConfirmDeleteId(null)}
            >
                Vill du verkligen ta bort ordern?
            </ApproveDenyModal>
        )}
        {pendingAction && (
            <TorkPicker
                torks={torks}
                noteTypes={noteTypes || []}
                value={pendingAction.order.torkId || null}
                title="Välj tork för att fortsätta"
                subtitle="Ordern saknar en vald tork. Välj en tork för att ändra status."
                onChange={(torkId) => commitPendingAction(torkId)}
                onClose={() => setPendingAction(null)}
            />
        )}
        </>
    );
};

export default OrdersTable;
